import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
	boardPinnedStreamsState,
	videoPinnedStreamsState,
	videoHiddenStreamsState,
	streamsState,
} from '@/recoil/atom/streamState';
import { hauntingTargetAtom } from '@/recoil/atom/hauntingState';
import { roleSelector } from '@/recoil/atom/userStatus';
import { useViewerContext } from '@/contexts/useViewer';
import { customBugsnagLogger } from '@/utils/bugsnag';

const usePinningManager = () => {
	const [boardPinnedStreams, setBoardPinnedStreams] = useRecoilState(boardPinnedStreamsState);
	const [videoPinnedStreams, setVideoPinnedStreams] = useRecoilState(videoPinnedStreamsState);
	const [videoHiddenStreams, setVideoHiddenStreams] = useRecoilState(videoHiddenStreamsState);
	const streams = useRecoilValue(streamsState);
	const streamsRef = useRef<{ [streamId: string]: OT.Stream }>();
	const boardPinnedRef = useRef<string[]>();
	const { viewer } = useViewerContext();
	const { id } = viewer;
	const sessionRole = useRecoilValue(roleSelector) || '';
	const hauntingTarget = useRecoilValue(hauntingTargetAtom);
	// A ghost cannot manually pin the board while actively haunting — their pin
	// state mirrors the haunted user and must not be mutated by hand. NOTE: this
	// only guards toggleBoardPin (a purely user-initiated action). Video pinning
	// is NOT guarded here because useSession drives connection-mirroring auto-pins
	// through toggleVideoPin; that internal flow must keep working while haunting.
	// The ghost's manual video-pin controls are disabled in the UI instead.
	const pinningBlocked =
		['ghost', 'super-ghost'].includes(sessionRole) &&
		hauntingTarget.targetId !== null;

	useEffect(() => {
		streamsRef.current = streams;
		return () => {
			streamsRef.current = undefined;
		};
	}, [streams]);

	useEffect(() => {
		boardPinnedRef.current = boardPinnedStreams;
		return () => {
			boardPinnedRef.current = undefined;
		};
	}, [boardPinnedStreams]);

	const checkBoardPinned = () => {
		if (!streamsRef.current) return;
		const streamIds = Object.keys(streamsRef.current);
		// Guard against the transient empty-map window: when a student disconnects
		// or the session reconnects, streamsState is briefly emptied/reset. If we
		// re-verify pins against an empty map we'd drop EVERY pin (cascade unpin)
		// and persist that to localStorage. Skip verification until streams exist
		// again — a genuinely-gone stream is pruned on the next real check.
		if (streamIds.length === 0) return;
		const verifiedStreams = boardPinnedRef?.current?.filter((pin) =>
			streamIds.includes(pin)
		);
		setBoardPinnedStreams(verifiedStreams);
	};

	const delayedBoardUnpin = (userId: string) => {
		if (!boardPinnedRef?.current?.includes(userId)) return;

		setTimeout(() => {
			// Only act if the user is still gone after the grace period — a quick
			// reconnect within 10s should keep the pin. checkBoardPinned then
			// re-verifies the full list, but is itself guarded against the
			// transient empty-map window so it won't cascade-unpin other students.
			if (streamsRef.current?.[userId]) return;
			checkBoardPinned();
		}, 10000);
	};

	const handleSetBoardPinned = (ids: string[]) => {
		setBoardPinnedStreams(ids);
	};

	const toggleBoardPin = (userId: string, pinned: boolean) => {
		if (pinningBlocked) return;
		if (!pinned) {
			customBugsnagLogger.debug(
				`Instructor: ${id} unpinned board for student: ${userId} at ${new Date().toISOString()} `
			);
			const newStreams = boardPinnedRef?.current?.filter(
				(stream) => stream !== userId
			);
			setBoardPinnedStreams(newStreams);
		} else {
			customBugsnagLogger.debug(
				`Instructor: ${id} pinned board for student: ${userId} at ${new Date().toISOString()} `
			);
			setBoardPinnedStreams((newStreams) => [...newStreams, userId]);
		}
	};

	// --- Video feed pin management ---

	const toggleVideoPin = (userId: string, pinned: boolean) => {
		if (pinned) {
			customBugsnagLogger.debug(
				`Instructor: ${id} force-pinned video for student: ${userId} at ${new Date().toISOString()} `
			);
			setVideoPinnedStreams((prev) =>
				prev.includes(userId) ? prev : [...prev, userId]
			);
			// Pinning overrides any force-hide
			setVideoHiddenStreams((prev) => prev.filter((s) => s !== userId));
		} else {
			customBugsnagLogger.debug(
				`Instructor: ${id} removed video pin for student: ${userId} at ${new Date().toISOString()} `
			);
			setVideoPinnedStreams((prev) => prev.filter((s) => s !== userId));
		}
	};

	const hideVideoFeed = (userId: string) => {
		customBugsnagLogger.debug(
			`Instructor: ${id} force-hid video for student: ${userId} at ${new Date().toISOString()} `
		);
		setVideoHiddenStreams((prev) =>
			prev.includes(userId) ? prev : [...prev, userId]
		);
		// Remove from force-pinned if it was there
		setVideoPinnedStreams((prev) => prev.filter((s) => s !== userId));
	};

	const showVideoFeed = (userId: string) => {
		setVideoHiddenStreams((prev) => prev.filter((s) => s !== userId));
	};

	const unpinAllBoards = () => {
		customBugsnagLogger.debug(
			`Instructor: ${id} unpinned all boards at ${new Date().toISOString()} `
		);
		setBoardPinnedStreams([]);
	};

	return {
		delayedBoardUnpin,
		handleSetBoardPinned,
		toggleBoardPin,
		checkBoardPinned,
		toggleVideoPin,
		hideVideoFeed,
		showVideoFeed,
		unpinAllBoards,
	};
};

export default usePinningManager;
